// ============================================================
// systems/phase3.js — Phase 3: Abstraction (3500+ clicks)
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { ABS_COLORS } from '../constants.js';
import { startMusic, stopMusic } from '../audio/musicChill.js';
import { startOgMusic, stopOgMusic } from '../audio/musicOg.js';
import { registerDeactivator } from './bombs.js';

// Lazy references set via registerPhase3Attacks() to avoid circular import
let _startAbsAttacks = null;
let _stopAbsAttacks = null;

/**
 * Called by phase3Attacks.js at init time to wire up the cross-reference.
 */
export function registerPhase3Attacks(startFn, stopFn) {
  _startAbsAttacks = startFn;
  _stopAbsAttacks = stopFn;
}

// ---- Helpers ----

function startCurrentMusic() {
  if (state.musicSource === 'mp3') { startMusic(); }
  else { startOgMusic(); }
}

// ---- Abstraction background ----

export function spawnAbsOrb() {
  const orb = document.createElement('div');
  orb.className = 'abs-orb';
  const size = 40 + Math.random() * 200;
  const color = ABS_COLORS[Math.floor(Math.random() * ABS_COLORS.length)];
  orb.style.width = size + 'px';
  orb.style.height = size + 'px';
  orb.style.left = Math.random() * 100 + '%';
  orb.style.top = Math.random() * 100 + '%';
  orb.style.background = `radial-gradient(circle, ${color}88, ${color}22, transparent)`;
  orb.style.filter = `blur(${8 + Math.random() * 20}px)`;
  orb.style.setProperty('--dx1', (Math.random() - 0.5) * 300 + 'px');
  orb.style.setProperty('--dy1', (Math.random() - 0.5) * 300 + 'px');
  orb.style.setProperty('--dx2', (Math.random() - 0.5) * 400 + 'px');
  orb.style.setProperty('--dy2', (Math.random() - 0.5) * 400 + 'px');
  orb.style.setProperty('--dx3', (Math.random() - 0.5) * 300 + 'px');
  orb.style.setProperty('--dy3', (Math.random() - 0.5) * 300 + 'px');
  orb.style.setProperty('--s1', (0.6 + Math.random() * 0.8).toFixed(2));
  orb.style.setProperty('--s2', (0.8 + Math.random() * 0.6).toFixed(2));
  orb.style.animationDuration = (8 + Math.random() * 12) + 's';
  orb.style.animationDelay = (Math.random() * -10) + 's';
  dom.abstractionBg.appendChild(orb);
  // Recycle after a while
  setTimeout(() => { orb.remove(); if (state.phase3Active) spawnAbsOrb(); }, 15000 + Math.random() * 10000);
}

export function spawnAbsRing() {
  const ring = document.createElement('div');
  ring.className = 'abs-ring';
  const size = 80 + Math.random() * 250;
  const color = ABS_COLORS[Math.floor(Math.random() * ABS_COLORS.length)];
  ring.style.width = size + 'px';
  ring.style.height = size + 'px';
  ring.style.left = Math.random() * 100 + '%';
  ring.style.top = Math.random() * 100 + '%';
  ring.style.borderColor = color;
  ring.style.animationDuration = (6 + Math.random() * 10) + 's';
  ring.style.animationDelay = (Math.random() * -5) + 's';
  dom.abstractionBg.appendChild(ring);
  setTimeout(() => { ring.remove(); if (state.phase3Active) spawnAbsRing(); }, 12000 + Math.random() * 8000);
}

export function spawnAbsLine() {
  const line = document.createElement('div');
  line.className = 'abs-line';
  const color = ABS_COLORS[Math.floor(Math.random() * ABS_COLORS.length)];
  line.style.top = Math.random() * 100 + '%';
  line.style.width = (100 + Math.random() * 300) + 'px';
  line.style.background = `linear-gradient(90deg, transparent, ${color}66, transparent)`;
  line.style.animationDuration = (2 + Math.random() * 3) + 's';
  dom.abstractionBg.appendChild(line);
  setTimeout(() => line.remove(), 5000);
}

// ---- Plushie movement ----

export function startPlushieMovement() {
  dom.smiley.style.position = 'absolute';
  movePlushie();
  const mobile = window.innerWidth <= 768;
  const base = mobile ? 3000 : 2000;
  const range = mobile ? 2250 : 1500;
  state.plushieMoveInterval = setInterval(movePlushie, base + Math.random() * range);
}

function movePlushie() {
  if (!state.phase3Active) return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const mobile = vw <= 768;
  const pw = mobile ? 100 : 180;
  const ph = mobile ? 125 : 220;
  const margin = mobile ? vw * 0.15 : 60;
  const minX = margin;
  const maxX = vw - pw - margin;
  const minY = mobile ? 60 : 80;
  const maxY = vh - ph - (mobile ? 40 : 30);
  dom.smiley.style.left = (minX + Math.random() * (maxX - minX)) + 'px';
  dom.smiley.style.top = (minY + Math.random() * (maxY - minY)) + 'px';
}

export function stopPlushieMovement() {
  clearInterval(state.plushieMoveInterval);
  state.plushieMoveInterval = null;
}

// ---- Plushie glitch effects ----

export function startPlushieGlitch() {
  state.plushieGlitchInterval = setInterval(() => {
    if (!state.phase3Active) return;
    const wrap = dom.smiley.querySelector('.fire-wrap');
    if (!wrap) return;

    // Glitch colored pixels
    for (let i = 0; i < 3; i++) {
      const px = document.createElement('div');
      px.className = 'plushie-glitch-pixel';
      const s = 4 + Math.random() * 12;
      px.style.width = s + 'px';
      px.style.height = s + 'px';
      px.style.left = Math.random() * 160 + 'px';
      px.style.top = Math.random() * 200 + 'px';
      const colors = ['#ff006e', '#8338ec', '#3a86ff', '#ffbe0b', '#00f5d4', '#f72585', '#ff0000'];
      px.style.background = colors[Math.floor(Math.random() * colors.length)];
      px.style.setProperty('--gx', (Math.random() - 0.5) * 40 + 'px');
      px.style.setProperty('--gy', (Math.random() - 0.5) * 40 + 'px');
      px.style.mixBlendMode = 'screen';
      wrap.appendChild(px);
      setTimeout(() => px.remove(), 300);
    }

    // Horizontal glitch slice on the plushie
    if (Math.random() > 0.5) {
      const slice = document.createElement('div');
      slice.className = 'plushie-glitch';
      const h = 3 + Math.random() * 8;
      slice.style.width = '100%';
      slice.style.height = h + 'px';
      slice.style.top = Math.random() * 200 + 'px';
      slice.style.left = '0';
      const color = Math.random() > 0.5 ? 'rgba(255,0,100,0.4)' : 'rgba(0,200,255,0.4)';
      slice.style.background = color;
      slice.style.animation = 'glitchShift 0.15s ease forwards';
      wrap.appendChild(slice);
      setTimeout(() => slice.remove(), 150);
    }
  }, 100);
}

export function stopPlushieGlitch() {
  clearInterval(state.plushieGlitchInterval);
  state.plushieGlitchInterval = null;
}

// ---- Activate / Deactivate ----

export function activatePhase3() {
  if (state.phase3Active) return;
  state.phase3Active = true;

  // Show warning
  dom.phase3Warning.classList.add('show');
  setTimeout(() => dom.phase3Warning.classList.remove('show'), 3500);

  // Keep the current character image — just tint it with a random hue
  // and apply glitch effects via the .abstracted class.
  const plushieImg = dom.smiley.querySelector('img');
  state.originalPlushieSrc = plushieImg.src;
  const randomHue = Math.floor(Math.random() * 360);
  dom.smiley.style.setProperty('--abs-hue', randomHue + 'deg');
  dom.smiley.classList.remove('frozen');
  dom.smiley.classList.add('abstracted');

  // Make plushie move randomly
  startPlushieMovement();

  // Start glitch particles on plushie
  startPlushieGlitch();

  // Start abstraction attacks
  if (_startAbsAttacks) _startAbsAttacks();

  // Activate abstraction background
  document.body.classList.add('phase3-mode');
  dom.abstractionBg.classList.add('active');

  // Swap the chill/og background for the phase-3 track.  Only start
  // playback if the user actually has music enabled — otherwise we'd
  // override their toggle.
  stopMusic();
  stopOgMusic();
  if (dom.toggleMusic.classList.contains('on')) {
    dom.phase3Audio.volume = state.musicVolume;
    dom.phase3Audio.currentTime = 0;
    dom.phase3Audio.play().catch((e) => console.warn('[phase3] audio play blocked:', e?.message || e));
  }

  // Spawn initial orbs
  for (let i = 0; i < 12; i++) {
    spawnAbsOrb();
  }
  // Spawn rings
  for (let i = 0; i < 6; i++) {
    spawnAbsRing();
  }

  // Keep spawning orbs and glitch lines
  state.absOrbInterval = setInterval(() => {
    if (dom.abstractionBg.children.length < 30) spawnAbsOrb();
    if (Math.random() > 0.5) spawnAbsLine();
  }, 800);

  state.absGlitchInterval = setInterval(() => {
    const g = document.createElement('div');
    g.className = 'abs-glitch';
    g.style.top = Math.random() * 100 + 'vh';
    document.body.appendChild(g);
    setTimeout(() => g.remove(), 100);
  }, 300 + Math.random() * 500);
}

export function deactivatePhase3() {
  if (!state.phase3Active) return;
  state.phase3Active = false;
  dom.abstractionBg.classList.remove('active');
  dom.abstractionBg.innerHTML = '';
  clearInterval(state.absOrbInterval);
  clearInterval(state.absGlitchInterval);
  dom.phase3Audio.pause();
  dom.phase3Audio.currentTime = 0;
  document.body.classList.remove('phase3-mode');

  // Bring back the chill/og music — but only if the player is dropping
  // back into the "normal" band (phase 4/5 handle their own tracks and
  // activate immediately after phase 3 deactivates on the way up).
  if (!state.phase4Active && !state.phase5Active &&
      dom.toggleMusic.classList.contains('on')) {
    startCurrentMusic();
  }

  // Restore plushie
  dom.smiley.classList.remove('abstracted');
  dom.smiley.style.removeProperty('--abs-hue');
  if (state.originalPlushieSrc) {
    dom.smiley.querySelector('img').src = state.originalPlushieSrc;
  }
  dom.smiley.style.position = '';
  dom.smiley.style.left = '';
  dom.smiley.style.top = '';
  stopPlushieMovement();
  stopPlushieGlitch();
  if (_stopAbsAttacks) _stopAbsAttacks();

  // Chill music was never stopped — keep it playing (ensure it's on
  // if the user had music enabled).
  if (dom.toggleMusic.classList.contains('on')) {
    startCurrentMusic();
  }
}

// Register phase3 deactivator so bombs.js can call it without circular import
registerDeactivator('phase3', deactivatePhase3);
